import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAllSousSegments } from '@/hooks/queries/usePerimetres';
import type { ParentSegmentType } from '@/lib/database.types';

export type FormationSegmentValue = 'all' | 'B2B' | 'B2C' | 'CBTA';

const FORMATION_SEGMENTS: { value: FormationSegmentValue; label: string; emoji: string }[] = [
  { value: 'all', label: 'Tous (consolidé)', emoji: '🌐' },
  { value: 'B2B', label: 'Kost B2B', emoji: '💼' },
  { value: 'B2C', label: 'Kost B2C', emoji: '🎓' },
  { value: 'CBTA', label: 'CBTA Provider', emoji: '✈️' },
];

interface Props {
  value: FormationSegmentValue;
  onChange: (v: FormationSegmentValue) => void;
}

/** Sélecteur métier pour le module Formation : Kost B2B / Kost B2C / CBTA Provider.
 *  Remplace le sélecteur entreprise (qui n'a pas de sens ici puisque seule
 *  KOST Academy fait de la formation).
 *  Persiste le choix dans l'URL via ?segment=B2B.
 */
export function SelecteurSousSegmentFormation({ value, onChange }: Props): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();

  // Sync URL → state
  useEffect(() => {
    const q = searchParams.get('segment') as FormationSegmentValue | null;
    if (q && FORMATION_SEGMENTS.some((s) => s.value === q) && q !== value) {
      onChange(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <select
      value={value}
      onChange={(e) => {
        const v = e.target.value as FormationSegmentValue;
        onChange(v);
        const next = new URLSearchParams(searchParams);
        if (v === 'all') next.delete('segment');
        else next.set('segment', v);
        setSearchParams(next, { replace: true });
      }}
      className="h-9 rounded-md border border-input bg-background px-2 text-sm"
      aria-label="Sous-segment Formation"
    >
      {FORMATION_SEGMENTS.map((s) => (
        <option key={s.value} value={s.value}>
          {s.emoji} {s.label}
        </option>
      ))}
    </select>
  );
}

/** Hook utilitaire : retourne les IDs de sous-segments Formation correspondant
 *  à la sélection courante. Filtre par parent_segment IN ('B2B','B2C','CBTA').
 *  Si segment='all' → tous les sous-segments Formation (B2B + B2C + CBTA). */
export function useSousSegmentsFormation(segment: FormationSegmentValue): {
  ids: string[];
  loading: boolean;
} {
  const { data: sousSegments = [], isLoading } = useAllSousSegments();
  const ids = useMemo(() => {
    const segments: ParentSegmentType[] =
      segment === 'all' ? ['B2B', 'B2C', 'CBTA'] : [segment as ParentSegmentType];
    return sousSegments
      .filter((s) => s.parent_segment && segments.includes(s.parent_segment))
      .map((s) => s.id);
  }, [sousSegments, segment]);
  return { ids, loading: isLoading };
}
